import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { useI18n } from "../context/I18nContext";
import { useToast } from "../components/Toast";
import { isPhotoImage } from "../utils/images";

const STATUSES = ["approved", "rejected", "pending"];
const USAGE_TYPES = [
  "dietitian_question",
  "diet_plan_action",
  "daily_plan_refresh",
  "personalized_daily_plan",
  "ingredient_ai_suggest",
  "ingredient_recipe_search",
  "recipe_detail_view",
  "photo_nutrition_estimate",
  "nutrition_text_estimate",
  "nutrition_search",
  "fitness_plan"
];
const LIMIT_FIELDS = ["guestFree", "userFree", "premiumFree"];
const INTERSTITIAL_FIELDS = [
  "dailyGuestLimit",
  "dailyUserLimit",
  "dailyPremiumLimit",
  "minGapSeconds"
];
const TEST_USER_FILTERS = ["all", "active", "inactive", "recent14"];

function formatAdminDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16).replace("T", " ");
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export default function AdminPanel({ navigate }) {
  const { state, actions } = useApp();
  const { t } = useI18n();
  const { toast } = useToast();
  const [status, setStatus] = useState("approved");
  const [recipes, setRecipes] = useState([]);
  const [copyrightReports, setCopyrightReports] = useState([]);
  const [testUserFilter, setTestUserFilter] = useState("all");
  const [testUsersData, setTestUsersData] = useState({ summary: {}, users: [] });
  const [testUsersLoading, setTestUsersLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [themealdbDraft, setThemealdbDraft] = useState({ firstLetter: "a", limit: "12" });
  const [designDraft, setDesignDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  useEffect(() => {
    if (state.settings) setDesignDraft(state.settings);
  }, [state.settings]);

  useEffect(() => {
    const load = async () => {
      if (!state.user?.isAdmin) return;
      setLoading(true);
      try {
        const data = await actions.getAdminRecipes(status);
        setRecipes(data);
        setDrafts(Object.fromEntries(data.map((recipe) => [
          recipe.id,
          {
            ingredientsText: recipe.ingredients.join("\n"),
            stepsText: recipe.steps.join("\n")
          }
        ])));
      } catch (error) {
        toast(error.message, "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [status, state.user?.isAdmin]);

  useEffect(() => {
    const loadReports = async () => {
      if (!state.user?.isAdmin) return;
      try {
        const data = await actions.getAdminCopyrightReports("new");
        setCopyrightReports(data || []);
      } catch (error) {
        toast(error.message, "error");
      }
    };
    loadReports();
  }, [state.user?.isAdmin]);

  const loadTestUsers = async () => {
    if (!state.user?.isAdmin) return;
    setTestUsersLoading(true);
    try {
      const data = await actions.getAdminTestUsers(testUserFilter);
      setTestUsersData({
        summary: data?.summary || {},
        users: data?.users || []
      });
    } catch (error) {
      toast(error.message, "error");
      setTestUsersData({ summary: {}, users: [] });
    } finally {
      setTestUsersLoading(false);
    }
  };

  useEffect(() => {
    loadTestUsers();
  }, [testUserFilter, state.user?.isAdmin]);

  const updateDraft = (recipeId, key, value) => {
    setDrafts((previous) => ({
      ...previous,
      [recipeId]: {
        ...(previous[recipeId] || {}),
        [key]: value
      }
    }));
  };

  const importThemealdb = async () => {
    setBusyId("themealdb");
    try {
      const imported = await actions.importThemealdbRecipes(themealdbDraft);
      if (status === "approved") {
        setRecipes((previous) => [
          ...imported,
          ...previous.filter((recipe) => !imported.some((item) => item.id === recipe.id))
        ]);
        setDrafts((previous) => ({
          ...previous,
          ...Object.fromEntries(imported.map((recipe) => [
            recipe.id,
            {
              ingredientsText: (recipe.ingredients || []).join("\n"),
              stepsText: (recipe.steps || []).join("\n")
            }
          ]))
        }));
      }
      toast(`${imported.length} ${t("themealdbImportDone")}`, "success");
    } catch (error) {
      toast(error.message, "error");
    } finally {
      setBusyId("");
    }
  };

  const moderate = async (recipeId, nextStatus) => {
    setBusyId(recipeId);
    try {
      await actions.moderateRecipe(recipeId, nextStatus);
      toast(nextStatus === "approved" ? t("recipeRepublished") : t("recipeUnpublished"), "success");
      setRecipes((previous) => previous.filter((recipe) => recipe.id !== recipeId));
    } catch (error) {
      toast(error.message, "error");
    } finally {
      setBusyId("");
    }
  };

  const uploadImage = async (recipeId, file) => {
    if (!file) return;
    setBusyId(recipeId);
    try {
      const updated = await actions.uploadAdminRecipeImage(recipeId, file);
      setRecipes((previous) => previous.map((recipe) => recipe.id === recipeId ? updated : recipe));
      toast(t("recipeImageUpdated"), "success");
    } catch (error) {
      toast(error.message, "error");
    } finally {
      setBusyId("");
    }
  };

  const updateMedia = async (recipeId, media, messageKey = "recipeMediaUpdated") => {
    setBusyId(recipeId);
    try {
      const updated = await actions.updateAdminRecipeMedia(recipeId, media);
      setRecipes((previous) => previous.map((recipe) => recipe.id === recipeId ? updated : recipe));
      toast(t(messageKey), "success");
    } catch (error) {
      toast(error.message, "error");
    } finally {
      setBusyId("");
    }
  };

  const updateReportStatus = async (reportId, nextStatus) => {
    setBusyId(reportId);
    try {
      await actions.updateAdminCopyrightReport(reportId, nextStatus);
      setCopyrightReports((previous) => previous.filter((report) => report.id !== reportId));
      toast(t("recipeContentUpdated"), "success");
    } catch (error) {
      toast(error.message, "error");
    } finally {
      setBusyId("");
    }
  };

  const saveContent = async (recipeId) => {
    const draft = drafts[recipeId] || {};
    const ingredients = String(draft.ingredientsText || "").split(/\n|,/).map((item) => item.trim()).filter(Boolean);
    const steps = String(draft.stepsText || "").split(/\n/).map((item) => item.trim()).filter(Boolean);

    setBusyId(recipeId);
    try {
      const updated = await actions.updateAdminRecipeContent(recipeId, { ingredients, steps });
      setRecipes((previous) => previous.map((recipe) => recipe.id === recipeId ? updated : recipe));
      setDrafts((previous) => ({
        ...previous,
        [recipeId]: {
          ingredientsText: updated.ingredients.join("\n"),
          stepsText: updated.steps.join("\n")
        }
      }));
      toast(t("recipeContentUpdated"), "success");
    } catch (error) {
      toast(error.message, "error");
    } finally {
      setBusyId("");
    }
  };

  const updateDesign = (key, value) => {
    setDesignDraft((previous) => ({ ...(previous || {}), [key]: value }));
  };

  const updateNavIcon = (key, value) => {
    setDesignDraft((previous) => ({
      ...(previous || {}),
      navIcons: {
        ...((previous || {}).navIcons || {}),
        [key]: value
      }
    }));
  };

  const updateUsageLimit = (usageType, field, value) => {
    setDesignDraft((previous) => ({
      ...(previous || {}),
      usageLimits: {
        ...((previous || {}).usageLimits || {}),
        [usageType]: {
          ...(((previous || {}).usageLimits || {})[usageType] || {}),
          [field]: value
        }
      }
    }));
  };

  const updateInterstitialRule = (field, value) => {
    setDesignDraft((previous) => ({
      ...(previous || {}),
      interstitialRules: {
        ...((previous || {}).interstitialRules || {}),
        [field]: value
      }
    }));
  };

  const updateAdSetting = (field, value) => {
    setDesignDraft((previous) => ({
      ...(previous || {}),
      adSettings: {
        ...((previous || {}).adSettings || {}),
        [field]: value
      }
    }));
  };

  const updateAppUpdateSetting = (platform, field, value) => {
    setDesignDraft((previous) => ({
      ...(previous || {}),
      appUpdate: {
        ...((previous || {}).appUpdate || {}),
        [platform]: {
          ...(((previous || {}).appUpdate || {})[platform] || {}),
          [field]: value
        }
      }
    }));
  };

  const saveDesign = async () => {
    if (!designDraft) return;
    setBusyId("design");
    try {
      await actions.updateAdminSettings(designDraft);
      toast(t("designSettingsSaved"), "success");
    } catch (error) {
      toast(error.message, "error");
    } finally {
      setBusyId("");
    }
  };

  if (!state.user?.isAdmin) {
    return (
      <div className="empty-state">
        <div className="empty-icon">Admin</div>
        <h3>{t("adminAccessRequired")}</h3>
        <p>{t("adminAccessHint")}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t("adminPanel")}</h1>
        <p>{t("adminPanelIntro")}</p>
      </div>

      <section className="admin-design-panel test-users-panel">
        <div className="admin-recipe-head">
          <strong>{t("testUsersTitle")}</strong>
          <span>{t("testUsersHint")}</span>
        </div>

        <div className="test-user-summary-grid">
          <SummaryCard label={t("testUsersTotal")} value={testUsersData.summary.totalUsers || 0} />
          <SummaryCard label={t("testUsersLoggedIn")} value={testUsersData.summary.loggedInUsers || 0} />
          <SummaryCard label={t("testUsersActive14")} value={testUsersData.summary.activeLast14Users || 0} />
          <SummaryCard label={t("testUsersOpenSessions")} value={testUsersData.summary.activeSessionUsers || 0} />
          <SummaryCard label={t("testUsersNeverLoggedIn")} value={testUsersData.summary.neverLoggedInUsers || 0} />
        </div>

        <div className="filter-bar test-user-filter-bar">
          <span className="filter-label">{t("testUsersFilter")}</span>
          <div className="filter-options">
            {TEST_USER_FILTERS.map((item) => (
              <button
                key={item}
                className={`filter-btn ${testUserFilter === item ? "active" : ""}`}
                onClick={() => setTestUserFilter(item)}
              >
                {t(`testUserFilter${item}`)}
              </button>
            ))}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={loadTestUsers} disabled={testUsersLoading}>
            {testUsersLoading ? t("loading") : t("refresh")}
          </button>
        </div>

        {testUsersLoading ? (
          <div className="loading compact"><div className="spinner" /><p>{t("loading")}</p></div>
        ) : testUsersData.users.length ? (
          <div className="test-user-table-wrap">
            <table className="test-user-table">
              <thead>
                <tr>
                  <th>{t("testUserEmail")}</th>
                  <th>{t("testUserId")}</th>
                  <th>{t("testUserFirstLogin")}</th>
                  <th>{t("testUserLastActive")}</th>
                  <th>{t("testUserSessionCount")}</th>
                  <th>{t("testUserSessionStatus")}</th>
                  <th>{t("testUserActiveDays14")}</th>
                  <th>{t("status")}</th>
                </tr>
              </thead>
              <tbody>
                {testUsersData.users.map((user) => (
                  <tr key={user.id}>
                    <td data-label={t("testUserEmail")}>
                      <strong>{user.email || user.name || "-"}</strong>
                      {user.name && user.email ? <span>{user.name}</span> : null}
                    </td>
                    <td data-label={t("testUserId")} className="mono-cell">{user.userId}</td>
                    <td data-label={t("testUserFirstLogin")}>{formatAdminDate(user.firstLoginAt)}</td>
                    <td data-label={t("testUserLastActive")}>{formatAdminDate(user.lastActiveAt)}</td>
                    <td data-label={t("testUserSessionCount")}>{user.sessionCount}</td>
                    <td data-label={t("testUserSessionStatus")}>
                      <span className={`status-pill ${user.hasActiveSession ? "active" : "inactive"}`}>
                        {user.hasActiveSession ? `${t("testUserSessionOpen")} (${user.activeSessionCount || 0})` : t("testUserSessionClosed")}
                      </span>
                    </td>
                    <td data-label={t("testUserActiveDays14")}>{user.activeDaysLast14}</td>
                    <td data-label={t("status")}>
                      <span className={`status-pill ${user.isActive ? "active" : "inactive"}`}>
                        {user.isActive ? t("active") : t("inactive")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">{t("noTestUsers")}</p>
        )}
      </section>

      {designDraft && (
        <section className="admin-design-panel">
          <div className="admin-recipe-head">
            <strong>{t("designSettings")}</strong>
            <span>{t("designSettingsIntro")}</span>
          </div>

          <div className="design-grid">
            <label>
              {t("appTitle")}
              <input value={designDraft.brandName || ""} onChange={(event) => updateDesign("brandName", event.target.value)} placeholder={t("brand")} />
            </label>
            <label>
              {t("bodyFont")}
              <select value={designDraft.bodyFont || ""} onChange={(event) => updateDesign("bodyFont", event.target.value)}>
                <option value="'DM Sans', sans-serif">DM Sans</option>
                <option value="Arial, sans-serif">Arial</option>
                <option value="Verdana, sans-serif">Verdana</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="'Times New Roman', serif">Times New Roman</option>
              </select>
            </label>
            <label>
              {t("headingFont")}
              <select value={designDraft.headingFont || ""} onChange={(event) => updateDesign("headingFont", event.target.value)}>
                <option value="'Playfair Display', serif">Playfair Display</option>
                <option value="Georgia, serif">Georgia</option>
                <option value="'DM Sans', sans-serif">DM Sans</option>
                <option value="Arial, sans-serif">Arial</option>
              </select>
            </label>
            <label>
              {t("baseFontSize")}
              <input value={designDraft.baseFontSize || ""} onChange={(event) => updateDesign("baseFontSize", event.target.value)} placeholder="16px" />
            </label>
            <label>
              {t("headingScale")}
              <input value={designDraft.headingScale || ""} onChange={(event) => updateDesign("headingScale", event.target.value)} placeholder="1" />
            </label>
            <label>
              {t("buttonRadius")}
              <input value={designDraft.buttonRadius || ""} onChange={(event) => updateDesign("buttonRadius", event.target.value)} placeholder="8px" />
            </label>
            <ColorInput label={t("primaryButtonColor")} value={designDraft.primaryColor} onChange={(value) => updateDesign("primaryColor", value)} />
            <ColorInput label={t("primaryButtonHover")} value={designDraft.primaryHoverColor} onChange={(value) => updateDesign("primaryHoverColor", value)} />
            <ColorInput label={t("successButtonColor")} value={designDraft.successColor} onChange={(value) => updateDesign("successColor", value)} />
            <ColorInput label={t("buttonTextColor")} value={designDraft.buttonTextColor} onChange={(value) => updateDesign("buttonTextColor", value)} />
          </div>

          <div className="nav-icon-grid">
            {["daily", "meals", "desserts", "ingredients", "nutrition", "addRecipe", "favorites", "shopping", "dietitian", "profile", "admin"].map((key) => (
              <label key={key}>
                {t(key === "ingredients" ? "ingredientSuggest" : key === "profile" ? "myProfile" : key)}
                <input value={designDraft.navIcons?.[key] || ""} onChange={(event) => updateNavIcon(key, event.target.value)} />
              </label>
            ))}
          </div>

          <div className="admin-settings-block">
            <h3>{t("usageLimitSettings")}</h3>
            <div className="usage-limit-table">
              <div className="usage-limit-head">
                <span>{t("usageType")}</span>
                {LIMIT_FIELDS.map((field) => <span key={field}>{t(field)}</span>)}
              </div>
              {USAGE_TYPES.map((usageType) => (
                <div className="usage-limit-row" key={usageType}>
                  <strong>{t(usageType)}</strong>
                  {LIMIT_FIELDS.map((field) => (
                    <input
                      key={field}
                      type="number"
                      min="0"
                      value={designDraft.usageLimits?.[usageType]?.[field] ?? ""}
                      onChange={(event) => updateUsageLimit(usageType, field, event.target.value)}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="admin-settings-block">
            <h3>{t("interstitialSettings")}</h3>
            <div className="design-grid">
              {INTERSTITIAL_FIELDS.map((field) => (
                <label key={field}>
                  {t(field)}
                  <input
                    type="number"
                    min="0"
                    value={designDraft.interstitialRules?.[field] ?? ""}
                    onChange={(event) => updateInterstitialRule(field, event.target.value)}
                  />
                </label>
              ))}
            </div>
          </div>

          <div className="admin-settings-block">
            <h3>{t("adMobSafetySettings")}</h3>
            <div className="design-grid">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={designDraft.adSettings?.consentRequired !== false}
                  onChange={(event) => updateAdSetting("consentRequired", event.target.checked)}
                />
                {t("consentRequired")}
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={designDraft.adSettings?.appAdsTxtRequired !== false}
                  onChange={(event) => updateAdSetting("appAdsTxtRequired", event.target.checked)}
                />
                {t("appAdsTxtRequired")}
              </label>
              <label>
                {t("appAdsTxtPublisherId")}
                <input value={designDraft.adSettings?.appAdsTxtPublisherId || ""} onChange={(event) => updateAdSetting("appAdsTxtPublisherId", event.target.value)} placeholder="pub-0000000000000000" />
              </label>
            </div>
          </div>

          <div className="admin-settings-block">
            <h3>{t("appUpdateSettings")}</h3>
            <div className="design-grid">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={designDraft.appUpdate?.enabled === true}
                  onChange={(event) => updateDesign("appUpdate", {
                    ...(designDraft.appUpdate || {}),
                    enabled: event.target.checked
                  })}
                />
                {t("appUpdateEnabled")}
              </label>
              {[
                ["android", "Android"],
                ["ios", "iOS"]
              ].map(([platform, label]) => (
                <React.Fragment key={platform}>
                  <label>
                    {label} {t("appUpdateLatestVersion")}
                    <input
                      value={designDraft.appUpdate?.[platform]?.latestVersion || ""}
                      onChange={(event) => updateAppUpdateSetting(platform, "latestVersion", event.target.value)}
                      placeholder="1.0.1"
                    />
                  </label>
                  <label>
                    {label} {t("appUpdateMinimumVersion")}
                    <input
                      value={designDraft.appUpdate?.[platform]?.minimumVersion || ""}
                      onChange={(event) => updateAppUpdateSetting(platform, "minimumVersion", event.target.value)}
                      placeholder="1.0.0"
                    />
                  </label>
                  <label>
                    {label} {t("appUpdateStoreUrl")}
                    <input
                      value={designDraft.appUpdate?.[platform]?.storeUrl || ""}
                      onChange={(event) => updateAppUpdateSetting(platform, "storeUrl", event.target.value)}
                      placeholder="https://..."
                    />
                  </label>
                </React.Fragment>
              ))}
            </div>
          </div>

          <button className="btn btn-primary" onClick={saveDesign} disabled={busyId === "design"}>
            {busyId === "design" ? t("saving") : t("saveDesignSettings")}
          </button>
        </section>
      )}

      <section className="admin-design-panel">
        <div className="admin-recipe-head">
          <strong>{t("themealdbImportTitle")}</strong>
          <span>{t("themealdbImportHint")}</span>
        </div>
        <div className="admin-design-grid">
          <label>
            {t("themealdbImportLetter")}
            <input
              maxLength={1}
              value={themealdbDraft.firstLetter}
              onChange={(event) => setThemealdbDraft((previous) => ({ ...previous, firstLetter: event.target.value.slice(0, 1) }))}
            />
          </label>
          <label>
            {t("themealdbImportLimit")}
            <input
              type="number"
              min="1"
              max="100"
              value={themealdbDraft.limit}
              onChange={(event) => setThemealdbDraft((previous) => ({ ...previous, limit: event.target.value }))}
            />
          </label>
        </div>
        <button className="btn btn-primary" onClick={importThemealdb} disabled={busyId === "themealdb"}>
          {busyId === "themealdb" ? t("saving") : t("themealdbImportButton")}
        </button>
      </section>

      <section className="admin-design-panel">
        <div className="admin-recipe-head">
          <strong>{t("copyrightReports")}</strong>
          <span>{t("copyrightReportsHint")}</span>
        </div>
        {copyrightReports.length ? (
          <div className="admin-list">
            {copyrightReports.map((report) => (
              <article className="admin-recipe" key={report.id}>
                <div>
                  <div className="admin-recipe-head">
                    <strong>{report.recipeTitle}</strong>
                    <span>{String(report.createdAt || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <p><strong>{t("copyrightIssueItems")}:</strong> {report.issueItems}</p>
                  {report.details ? <p>{report.details}</p> : null}
                  {report.referenceUrl ? <a href={report.referenceUrl} target="_blank" rel="noreferrer">{report.referenceUrl}</a> : null}
                  <div className="admin-meta">
                    <span>{report.reporterName || "-"}</span>
                    <span>{report.reporterEmail}</span>
                  </div>
                </div>
                <div className="admin-actions">
                  <button className="btn btn-outline" disabled={busyId === report.id} onClick={() => updateReportStatus(report.id, "reviewing")}>
                    {t("markReviewing")}
                  </button>
                  <button className="btn btn-primary" disabled={busyId === report.id} onClick={() => updateReportStatus(report.id, "removed")}>
                    {t("markRemoved")}
                  </button>
                  <button className="btn btn-ghost" disabled={busyId === report.id} onClick={() => updateReportStatus(report.id, "dismissed")}>
                    {t("markDismissed")}
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="muted">{t("noCopyrightReports")}</p>
        )}
      </section>

      <div className="filter-bar filter-panel">
        <div className="filter-row">
          <span className="filter-label">{t("status")}</span>
          <div className="filter-options">
            {STATUSES.map((item) => (
              <button
                key={item}
                className={`filter-btn ${status === item ? "active" : ""}`}
                onClick={() => setStatus(item)}
              >
                {t(item)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /><p>{t("loadingRecipes")}</p></div>
      ) : recipes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">OK</div>
          <h3>{t("noModerationRecipes")}</h3>
          <p>{t("noModerationRecipesHint")}</p>
        </div>
      ) : (
        <div className="admin-list">
          {recipes.map((recipe) => (
            <article className="admin-recipe" key={recipe.id}>
              <div>
                <div className={`admin-recipe-image ${isPhotoImage(recipe.image) ? "has-photo" : ""}`}>
                  {isPhotoImage(recipe.image) ? <img src={recipe.image} alt={recipe.title} /> : recipe.image}
                </div>
                <div className="admin-recipe-head">
                  <strong>{recipe.title}</strong>
                  <span>{recipe.cuisine} / {t(recipe.mealType)} / {Number(recipe.calories) > 0 ? `${recipe.calories} ${t("kcal")}` : t("nutritionUnverified")}</span>
                </div>
                <p>{recipe.description}</p>
                <div className="admin-meta">
                  <span>{t("status")}: {t(recipe.moderationStatus)}</span>
                  {recipe.youtubeUrl && <a href={recipe.youtubeUrl} target="_blank" rel="noreferrer">{t("videoRecipe")}</a>}
                </div>
                <details>
                  <summary>{t("ingredients")} / {t("steps")}</summary>
                  <div className="admin-edit-grid">
                    <label>
                      {t("ingredients")}
                      <textarea
                        value={drafts[recipe.id]?.ingredientsText || ""}
                        onChange={(event) => updateDraft(recipe.id, "ingredientsText", event.target.value)}
                      />
                    </label>
                    <label>
                      {t("steps")}
                      <textarea
                        value={drafts[recipe.id]?.stepsText || ""}
                        onChange={(event) => updateDraft(recipe.id, "stepsText", event.target.value)}
                      />
                    </label>
                    <button
                      className="btn btn-outline"
                      disabled={busyId === recipe.id}
                      onClick={() => saveContent(recipe.id)}
                    >
                      {t("saveChanges")}
                    </button>
                  </div>
                </details>
                <label className="admin-upload">
                  {t("changeImage")}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={busyId === recipe.id}
                    onChange={(event) => uploadImage(recipe.id, event.target.files?.[0])}
                  />
                </label>
                <div className="admin-actions">
                  <button className="btn btn-outline" disabled={busyId === recipe.id} onClick={() => updateMedia(recipe.id, { clearImage: true }, "recipeImageRemoved")}>
                    {t("removeImage")}
                  </button>
                  {recipe.youtubeUrl && (
                    <button className="btn btn-outline" disabled={busyId === recipe.id} onClick={() => updateMedia(recipe.id, { clearVideo: true }, "recipeVideoRemoved")}>
                      {t("removeVideo")}
                    </button>
                  )}
                  {recipe.stepImages?.length ? (
                    <button className="btn btn-outline" disabled={busyId === recipe.id} onClick={() => updateMedia(recipe.id, { clearStepImages: true }, "recipeStepImagesRemoved")}>
                      {t("removeStepImages")}
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="admin-actions">
                <button className="btn btn-outline" onClick={() => navigate("detail", recipe)}>{t("preview")}</button>
                {recipe.moderationStatus === "rejected" ? (
                  <button className="btn btn-primary" disabled={busyId === recipe.id} onClick={() => moderate(recipe.id, "approved")}>
                    {t("republishRecipe")}
                  </button>
                ) : (
                  <button className="btn btn-ghost" disabled={busyId === recipe.id} onClick={() => moderate(recipe.id, "rejected")}>
                    {t("unpublishRecipe")}
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function ColorInput({ label, value, onChange }) {
  return (
    <label>
      {label}
      <div className="color-input-row">
        <input type="color" value={value || "#E8703A"} onChange={(event) => onChange(event.target.value)} />
        <input value={value || ""} onChange={(event) => onChange(event.target.value)} />
      </div>
    </label>
  );
}

function SummaryCard({ label, value }) {
  return (
    <div className="test-user-summary-card">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
